import { useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

// Referrer hostname → normalized source. Anything not in this list falls
// through to "direct".
const REFERRER_MAP = [
  { match: /(?:^|\.)tiktok\.com$/i, source: "tiktok" },
  { match: /(?:^|\.)instagram\.com$/i, source: "instagram" },
  { match: /(?:^|\.)reddit\.com$/i, source: "reddit" },
  { match: /(?:^|\.)(?:google|goog)\.[a-z.]+$/i, source: "google" },
  { match: /(?:^|\.)(?:twitter|x)\.com$/i, source: "twitter" },
  { match: /(?:^|\.)facebook\.com$/i, source: "facebook" },
  { match: /(?:^|\.)(?:youtube|youtu)\.(?:com|be)$/i, source: "youtube" },
  { match: /(?:^|\.)discord\.(?:com|gg)$/i, source: "discord" },
  { match: /(?:^|\.)t\.co$/i, source: "twitter" },
  { match: /(?:^|\.)linkedin\.com$/i, source: "linkedin" },
];

function detectFromReferrer(ref) {
  if (!ref) return null;
  try {
    const host = new URL(ref).hostname;
    for (const rule of REFERRER_MAP) {
      if (rule.match.test(host)) return rule.source;
    }
  } catch {
    /* ignore malformed referrer */
  }
  return null;
}

// Stable visitor id — one UUID per browser (localStorage). Persists forever
// unless the user clears site data. This is what "unique visitor" counts —
// not IP, not session, not user account.
function getOrCreateVisitorId() {
  try {
    let vid = localStorage.getItem("nw_visitor_id");
    if (!vid) {
      vid =
        (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
          ? crypto.randomUUID()
          : `v-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`);
      localStorage.setItem("nw_visitor_id", vid);
      localStorage.setItem("nw_visitor_first_seen", new Date().toISOString());
    }
    return vid;
  } catch {
    return null;
  }
}

/**
 * Traffic-source tracker. Fires ONCE per browser tab session, always includes
 * the persistent visitor_id so the backend can distinguish real unique visitors
 * from page reloads / route changes. Also fires again when a user signs in or
 * upgrades so their prior anonymous hits get backfilled to their new user_id.
 *
 * Priority for the recorded source:
 *   1. Explicit `?source=X` URL param
 *   2. Auto-detect from document.referrer hostname
 *   3. Fallback to "direct"
 */
export function useTrafficSource() {
  const { user } = useAuth();

  useEffect(() => {
    if (typeof window === "undefined") return;
    let cancelled = false;

    const run = async () => {
      try {
        const SESSION_KEY = "nw_traffic_recorded";
        const lastUserKey = sessionStorage.getItem("nw_traffic_last_user") || "";
        const currentUserKey = user?.id || "";
        if (sessionStorage.getItem(SESSION_KEY) && lastUserKey === currentUserKey) return;

        const url = new URL(window.location.href);
        const rawParam = (url.searchParams.get("source") || "").trim().slice(0, 32);
        const referrer = document.referrer || "";
        const detected = detectFromReferrer(referrer);

        let sourceToSend;
        if (rawParam) {
          sourceToSend = rawParam;
        } else if (detected) {
          sourceToSend = detected;
        } else {
          sourceToSend = "direct";
        }

        // Strip ?source= from URL SYNCHRONOUSLY, before any await. StrictMode's
        // second effect-run can skip past a post-await strip if `cancelled` is
        // set — doing it here guarantees the URL is clean in dev + prod.
        if (rawParam) {
          url.searchParams.delete("source");
          window.history.replaceState({}, "", url.pathname + (url.search || "") + url.hash);
        }

        sessionStorage.setItem(SESSION_KEY, "1");
        sessionStorage.setItem("nw_traffic_last_user", currentUserKey);

        if (!localStorage.getItem("nw_traffic_source")) {
          const normalized = sourceToSend.toLowerCase().replace(/[^a-z0-9_-]/g, "") || "direct";
          localStorage.setItem("nw_traffic_source", normalized);
          localStorage.setItem("nw_traffic_source_at", new Date().toISOString());
        }

        const visitor_id = getOrCreateVisitorId();

        await api.post("/track/source", {
          source: sourceToSend,
          referrer: referrer || null,
          path: window.location.pathname,
          visitor_id,
        }).catch(() => { /* silent — never break UX on tracking failure */ });
      } catch {
        /* silent */
      }
    };
    run();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);
}
